import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Paper,
  Box,
} from "@mui/material";
import "./ResumenTurnos.css";

import EventIcon from "@mui/icons-material/Event";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { turnosService } from "../../service/turnosService.js";
import { useAuth } from "../../context/authContext.jsx";

const ResumenTurnos = ({ carrito, eliminarTurno }) => {
  const navigate = useNavigate();
  const { usuario } = useAuth();

  const [modalAbierto, setModalAbierto] = useState(false);
  const [turnoAEliminar, setTurnoAEliminar] = useState(null);

  // Estados para la reserva
  const [reservando, setReservando] = useState(false);
  const [errorReserva, setErrorReserva] = useState(null);
  const [exitoReserva, setExitoReserva] = useState(false);
  const [resultadoReserva, setResultadoReserva] = useState(null);

  const totalCosto = carrito.reduce((acc, turno) => acc + turno.costo, 0);

  //confirmar reserva de multiples turnos
  const confirmarReserva = async () => {
    if (carrito.length === 0) {
      setErrorReserva("No hay turnos para reservar");
      return;
    }

    setReservando(true);
    setErrorReserva(null);
    setExitoReserva(false);
    setResultadoReserva(null);

    try {
      const pacienteId = usuario?.pacienteId;

      if (!pacienteId) {
        setErrorReserva("No se encontró el ID del paciente. Iniciá sesión nuevamente.");
        setReservando(false);
        return;
      }

      const idsTurnos = carrito.map((turno) => turno.id);

      const resultado = await turnosService.reservarMultiples({
        idPaciente: pacienteId,
        idsTurnos: idsTurnos,
      });

      setResultadoReserva(resultado);

      if (resultado.totalReservados === 0) {
        const mensajes = resultado.errores
          .map((e) => `Turno ${e.idTurno}: ${e.error}`)
          .join("; ");
        throw new Error(`No se pudo reservar ningún turno: ${mensajes}`);
      }

      const reservadosIds = resultado.reservados.map((r) => r.id);
      carrito.forEach((turno) => {
        if (reservadosIds.includes(turno.id)) {
          eliminarTurno(turno.id);
        }
      });

      setExitoReserva(true);

      const tiempoRedireccion = resultado.totalErrores > 0 ? 3000 : 2000;
      setTimeout(() => {
        navigate("/", {
          state: {
            mensaje: resultado.mensaje,
            reservados: resultado.reservados,
            errores: resultado.errores,
            totalReservados: resultado.totalReservados,
            totalErrores: resultado.totalErrores,
          },
        });
      }, tiempoRedireccion);
    } catch (error) {
      console.error("Error en reserva:", error);
      setErrorReserva(error.message || "Error al confirmar la reserva");
      setExitoReserva(false);
    } finally {
      setReservando(false);
    }
  };

  const intentarEliminar = (turno) => {
    if (reservando || exitoReserva) return;
    setTurnoAEliminar(turno);
    setModalAbierto(true);
  };

  const cancelarEliminacion = () => {
    setModalAbierto(false);
    setTurnoAEliminar(null);
  };

  const confirmarEliminacion = () => {
    if (turnoAEliminar) {
      eliminarTurno(turnoAEliminar.id);
    }
    setModalAbierto(false);
  };

  // Estado vacío
  if (carrito.length === 0 && !exitoReserva) {
    return (
      <div className="resumen-vacio-container">
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          No tenés turnos preseleccionados
        </Typography>
        <Typography variant="body1" color="text.secondary" className="vacio-subtitulo">
          Explorá las especialidades disponibles para reservar tu lugar.
        </Typography>
        <Button variant="contained" color="primary" component={Link} to="/">
          Buscar Turnos
        </Button>
      </div>
    );
  }


  if (exitoReserva && resultadoReserva) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          textAlign: "center",
          padding: 4,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            padding: 5,
            borderRadius: 4,
            maxWidth: 550,
            width: "100%",
            backgroundColor: "#f0fdf4",
            border: "3px solid #4caf50",
            position: "relative",
            overflow: "hidden",
          }}
        >

          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              backgroundColor: "#4caf50",
            }}
          />


          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#4caf50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              mt: 1,
            }}
          >
            <Typography variant="h1" sx={{ fontSize: 48, color: "white", fontWeight: "bold" }}>
              ✓
            </Typography>
          </Box>


          <Typography variant="h3" fontWeight="bold" sx={{ color: "#2e7d32", mb: 1 }}>
            ¡Reserva confirmada!
          </Typography>


          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Tus turnos fueron reservados exitosamente.
            <br />
            Te recordaremos el día previo a la atención.
          </Typography>


          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
              p: 2,
              bgcolor: "rgba(76, 175, 80, 0.08)",
              borderRadius: 2,
            }}
          >
            <Box textAlign="center">
              <Typography variant="h2" fontWeight="bold" color="#2e7d32">
                {resultadoReserva.totalReservados}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Turnos reservados
              </Typography>
            </Box>
          </Box>


          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/dashboard-turnos"
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 4,
              }}
            >
              Ver mis turnos
            </Button>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to="/"
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 4,
              }}
            >
              Seguir buscando turnos
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <div className="resumen-page-container">
      <Typography
        variant="h4"
        fontWeight={600}
        className="resumen-titulo"
      >
        Resumen de tu preselección
      </Typography>

      <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4 }}
      >
          Revisá los turnos seleccionados antes de confirmar la reserva.
      </Typography>

      {errorReserva && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorReserva}
        </Alert>
      )}

      <div className="resumen-layout">
        <div className="lista-turnos-resumen">
          {carrito.map((turno) => (
            <Card key={turno.id} variant="outlined" className="turno-card-resumen">
              <CardContent className="item-content-resumen">
                <div className="info-medica-resumen">
                  <Typography variant="subtitle1" fontWeight="bold">
                    {turno.medico}
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight="600">
                    {turno.especialidad}
                  </Typography>
                  <div className="info-line">
                    <EventIcon className="info-icon" />
                    <Typography variant="caption" color="text.secondary">
                      {turno.fecha}
                    </Typography>
                  </div>
                  <div className="info-line">
                    <AccessTimeRoundedIcon className="info-icon" />
                    <Typography variant="caption" color="text.secondary">
                      {turno.hora} hs
                    </Typography>
                  </div>
                  <div className="info-line">
                    <LocationOnRoundedIcon className="info-icon" />
                    <Typography variant="caption" color="text.secondary">
                      {turno.sede}
                    </Typography>
                  </div>
                </div>

                <div className="acciones-item-resumen">
                  <Typography variant="body1" fontWeight="bold" className="costo-turno">
                    ${turno.costo.toLocaleString("es-AR")}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => intentarEliminar(turno)}
                    disabled={reservando}
                  >
                    Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="sidebar-total-resumen">
          <Card variant="outlined" className="sidebar-card">
            <Typography variant="h6" fontWeight="bold" className="sidebar-titulo">
              Detalle del pedido
            </Typography>

            <div className="sidebar-line">
              <Typography variant="body2" color="text.secondary">
                Turnos seleccionados:
              </Typography>
              <Typography variant="body2" fontWeight="600">
                {carrito.length}
              </Typography>
            </div>

            <Divider className="sidebar-divider" />

            <div className="sidebar-total-line">
              <Typography variant="body1" fontWeight="bold">
                Total estimado:
              </Typography>
              <Typography variant="h5" fontWeight="800" className="sidebar-total-monto">
                ${totalCosto.toLocaleString("es-AR")}
              </Typography>
            </div>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              className="confirmar-button"
              onClick={confirmarReserva}
              disabled={reservando || carrito.length === 0}
              startIcon={reservando && <CircularProgress size={20} color="inherit" />}
            >
              {reservando ? "Reservando..." : "Confirmar Reserva"}
            </Button>

            <Button
              variant="text"
              fullWidth
              component={Link}
              to="/"
              className="agregar-mas-button"
              disabled={reservando}
            >
              + Agregar más turnos
            </Button>
          </Card>
        </div>
      </div>

      <Dialog
        open={modalAbierto}
        onClose={cancelarEliminacion}
        PaperProps={{ className: "modal-paper" }}
      >
        <DialogTitle className="modal-titulo">
          <EventBusyRoundedIcon />
          Eliminar turno
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Estás por eliminar el turno con <strong>{turnoAEliminar?.medico}</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="modal-acciones">
          <Button onClick={cancelarEliminacion} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmarEliminacion} color="error" variant="contained">
            Eliminar turno
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ResumenTurnos;